import { Box, Button, Input, InputImperativeActions } from '@wix/design-system'
import { useContent, useNext, usePageLoading } from '@wix/funnel-builder-lizard'
import { GetStarted } from '@wix/wix-ui-icons-common'
import React, { ChangeEventHandler, FC, useEffect, useRef, useState } from 'react'

import { usePageLogger } from '../../../bi/mfi'
import { useStorageContext } from '../../../context/StorageContext/StorageContext'
import { useBrowserTabTitle } from '../../../hooks/useBrowserTabTitle'
import { ContentWithLoading } from '../../reusable/Button'
import { HeadlessTemplate } from '../../reusable/HeadlessTemplate'
import type { Content } from './Content'
import { headlessBusinessNameDataHooks as dataHooks } from './HeadlessBusinessName.dataHooks'
import s from './HeadlessBusinessName.scss'

export const HeadlessBusinessName: FC = () => {
  const { save } = useStorageContext()
  const { content } = useContent<Content>()
  const [value, setValue] = useState(content.question.content.value)
  const next = useNext()
  const [isPageLoading] = usePageLoading()
  const inputRef = useRef<InputImperativeActions>(null)

  useBrowserTabTitle(content.browserTabTitle)

  const pageLogger = usePageLogger()

  const handleValueChange: ChangeEventHandler<HTMLInputElement> = event => {
    if (isPageLoading) {
      return
    }
    setValue(event.target.value)
  }

  const handleSkipClick = () => {
    save({ businessName: '' })
    next({ [content.question.questionKey]: { skipped: true } })
  }

  const handleNext = () => {
    if (isPageLoading) {
      return
    }

    void pageLogger.answer({ answer: value })
    save({ businessName: value })
    next({ [content.question.questionKey]: { values: [value] } })
  }

  useEffect(() => {
    inputRef.current?.focus()
  }, [])

  return (
    <HeadlessTemplate
      isPageLoading={isPageLoading}
      pageLogger={pageLogger}
      stepText={content.stepText}
      title={content.title}
      subtitle={content.subtitle}
      cardTitle={content.cardTitle}
      backButton={content.backButton}
      skipButton={content.skipButton}
      onSkipClick={handleSkipClick}
      dataHook={dataHooks.template()}
    >
      <Box className={s.input}>
        <Input
          ref={inputRef}
          size="large"
          placeholder={content.question.content.placeholder}
          value={value}
          onChange={handleValueChange}
          dataHook={dataHooks.input()}
        />
      </Box>
      <Box className={s.button}>
        <Button size="large" onClick={handleNext} fullWidth suffixIcon={<GetStarted />} dataHook={dataHooks.next()}>
          <ContentWithLoading isLoading={isPageLoading}>{content.nextButton}</ContentWithLoading>
        </Button>
      </Box>
    </HeadlessTemplate>
  )
}
